import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import {
  dismissStaffNotification,
  listStaffNotifications,
} from '@/features/notifications/api/staff-notifications.http.js';
import { staffNotificationMatchesCheckout } from '@/features/notifications/lib/parse-staff-notification-deeplink.js';
import { staffNotificationKeys } from '@/features/notifications/query-keys.js';

/**
 * Opening a checkout screen consumes related staff inbox rows (pending + decision),
 * same pattern as joining a chat thread.
 */
export function useAckStaffCheckoutNotifications(serviceOrderId: string | undefined): void {
  const queryClient = useQueryClient();
  const ackedRef = useRef<string | null>(null);

  useEffect(() => {
    if (serviceOrderId === undefined || serviceOrderId.length === 0) {
      return;
    }
    if (ackedRef.current === serviceOrderId) {
      return;
    }

    void (async () => {
      try {
        const page = await listStaffNotifications({ limit: 30 });
        const matching = page.items.filter((item) =>
          staffNotificationMatchesCheckout(item.deeplink, serviceOrderId),
        );
        if (matching.length === 0) {
          ackedRef.current = serviceOrderId;
          return;
        }
        await Promise.all(matching.map((item) => dismissStaffNotification(item.id)));
        ackedRef.current = serviceOrderId;
        await queryClient.invalidateQueries({ queryKey: staffNotificationKeys.all });
      } catch {
        // Bell ack is best-effort; unread rows remain until the next open or manual dismiss.
      }
    })();
  }, [queryClient, serviceOrderId]);
}
